import {validatePublicKey} from 'metaserver/static/js/encrypted_folder/crypto/hpke/keyValidation';
import type {
  AsymmetricKeyPair,
  Encapsulation,
  PrivateKey,
  PublicKey,
  SealedDataWithEncappedKey,
} from 'metaserver/static/js/encrypted_folder/crypto/hpke/types';
import {HPKE_VERSION} from 'metaserver/static/js/encrypted_folder/crypto/hpke/types';
import {concat, i2osp} from 'metaserver/static/js/encrypted_folder/crypto/hpke/utils';

const MODE_BASE = new Uint8Array([0x00]);
const MODE_AUTH = new Uint8Array([0x02]);
export type HPKESchemeInfo = {
  kdfId: number;
  kemId: number;
  aeadId: number;
  nSecret: number;
  nK: number;
  nN: number;
  nEnc: number;
};

export interface HPKECryptoScheme {
  generateKeyPair(): Promise<AsymmetricKeyPair>;

  sealWithKey(props: {
    key: Uint8Array<ArrayBuffer>;
    nonce: Uint8Array<ArrayBuffer>;
    aad: Uint8Array<ArrayBuffer>;
    pt: Uint8Array<ArrayBuffer>;
  }): Promise<Uint8Array<ArrayBuffer>>;

  openWithKey(props: {
    key: Uint8Array<ArrayBuffer>;
    nonce: Uint8Array<ArrayBuffer>;
    aad: Uint8Array<ArrayBuffer>;
    ct: Uint8Array<ArrayBuffer>;
  }): Promise<Uint8Array<ArrayBuffer>>;

  serializePublicKey(pkX: CryptoKey): Promise<Uint8Array<ArrayBuffer>>;

  deserializePublicKey(pkXm: Uint8Array<ArrayBuffer>): Promise<CryptoKey>;

  extract(
    salt: Uint8Array<ArrayBuffer>,
    ikm: Uint8Array<ArrayBuffer>
  ): Promise<Uint8Array<ArrayBuffer>>;

  expand(
    prk: Uint8Array<ArrayBuffer>,
    info: Uint8Array,
    length: number
  ): Promise<Uint8Array<ArrayBuffer>>;

  dh(skX: PrivateKey, pkY: PublicKey): Promise<Uint8Array<ArrayBuffer>>;

  pkFromSk(sk: PrivateKey): Promise<PublicKey>;

  info(): HPKESchemeInfo;

  sha256(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array>;
}

export interface KeyEncodingScheme {
  toPKCS8(privateKey: CryptoKey): Promise<Uint8Array<ArrayBuffer>>;

  fromPKCS8(privateKey: Uint8Array<ArrayBuffer>): Promise<CryptoKey>;

  toDer(publicKey: CryptoKey): Promise<Uint8Array<ArrayBuffer>>;

  fromDer(publicKey: Uint8Array<ArrayBuffer>): Promise<CryptoKey>;

  encode(privateKey: CryptoKey): Promise<Uint8Array<ArrayBuffer>>;

  toJWK(publicKey: Uint8Array<ArrayBuffer>): Promise<JsonWebKey>;
}

export async function sealBase(
  hpkeScheme: HPKECryptoScheme,
  pkR: PublicKey,
  aad: Uint8Array<ArrayBuffer>,
  pt: Uint8Array<ArrayBuffer>
): Promise<SealedDataWithEncappedKey> {
  await validatePublicKey(pkR);
  const {encappedKey, sharedSecret} = await baseEncap(hpkeScheme, pkR);
  const {key, nonce} = await keySchedule(hpkeScheme, MODE_BASE, sharedSecret);
  const cipherText = await hpkeScheme.sealWithKey({key, nonce, aad, pt});

  return {
    encappedKey,
    cipherText,
  };
}

export async function sealAuth(
  hpkeScheme: HPKECryptoScheme,
  pkR: PublicKey,
  skS: PrivateKey,
  aad: Uint8Array<ArrayBuffer>,
  pt: Uint8Array<ArrayBuffer>
): Promise<SealedDataWithEncappedKey> {
  await validatePublicKey(pkR);
  const {encappedKey, sharedSecret} = await authEncap(hpkeScheme, pkR, skS);
  const {key, nonce} = await keySchedule(hpkeScheme, MODE_AUTH, sharedSecret);
  const cipherText = await hpkeScheme.sealWithKey({key, nonce, aad, pt});

  return {
    encappedKey,
    cipherText,
  };
}

export async function openBase(
  hpkeScheme: HPKECryptoScheme,
  enc: Uint8Array<ArrayBuffer>,
  skR: PrivateKey,
  aad: Uint8Array<ArrayBuffer>,
  ct: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  const sharedSecret = await baseDecap(hpkeScheme, enc, skR);
  const {key, nonce} = await keySchedule(hpkeScheme, MODE_BASE, sharedSecret);

  return await hpkeScheme.openWithKey({key, nonce, aad, ct});
}

export async function openAuth(
  hpkeScheme: HPKECryptoScheme,
  enc: Uint8Array<ArrayBuffer>,
  skR: PrivateKey,
  pkS: PublicKey,
  aad: Uint8Array<ArrayBuffer>,
  ct: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  const sharedSecret = await authDecap(hpkeScheme, enc, skR, pkS);
  const {key, nonce} = await keySchedule(hpkeScheme, MODE_AUTH, sharedSecret);

  return await hpkeScheme.openWithKey({key, nonce, aad, ct});
}

async function keySchedule(
  hpkeScheme: HPKECryptoScheme,
  mode: Uint8Array<ArrayBuffer>,
  sharedSecret: Uint8Array<ArrayBuffer>
): Promise<{key: Uint8Array<ArrayBuffer>; nonce: Uint8Array<ArrayBuffer>}> {
  const infoHash = await labeledExtract(
    hpkeScheme,
    fullSuiteId(hpkeScheme),
    new Uint8Array(0),
    new TextEncoder().encode('info_hash'),
    new Uint8Array(0)
  );
  const pskIdHash = await labeledExtract(
    hpkeScheme,
    fullSuiteId(hpkeScheme),
    new Uint8Array(0),
    new TextEncoder().encode('psk_id_hash'),
    new Uint8Array(0)
  );
  const keyScheduleContext = concat([mode, pskIdHash, infoHash]);
  const secret = await labeledExtract(
    hpkeScheme,
    fullSuiteId(hpkeScheme),
    sharedSecret,
    new TextEncoder().encode('secret'),
    new Uint8Array(0)
  );
  const key = await labeledExpand(
    hpkeScheme,
    fullSuiteId(hpkeScheme),
    secret,
    new TextEncoder().encode('key'),
    keyScheduleContext,
    hpkeScheme.info().nK
  );
  const baseNonce = await labeledExpand(
    hpkeScheme,
    fullSuiteId(hpkeScheme),
    secret,
    new TextEncoder().encode('base_nonce'),
    keyScheduleContext,
    hpkeScheme.info().nN
  );

  return {
    key: key,
    nonce: baseNonce,
  };
}

export function fullSuiteId(hpkeScheme: HPKECryptoScheme): Uint8Array<ArrayBuffer> {
  const hpkeString = new TextEncoder().encode('HPKE');
  const i2ospKem = i2osp(hpkeScheme.info().kemId, 2);
  const i2ospKdf = i2osp(hpkeScheme.info().kdfId, 2);
  const i2ospAead = i2osp(hpkeScheme.info().aeadId, 2);
  return concat([hpkeString, i2ospKem, i2ospKdf, i2ospAead]);
}

async function labeledExtract(
  hpkeScheme: HPKECryptoScheme,
  suiteId: Uint8Array<ArrayBuffer>,
  salt: Uint8Array<ArrayBuffer>,
  label: Uint8Array<ArrayBuffer>,
  ikm: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  const labeledIkm = concat([HPKE_VERSION, suiteId, label, ikm]);
  return await hpkeScheme.extract(salt, labeledIkm);
}

async function labeledExpand(
  hpkeScheme: HPKECryptoScheme,
  suiteId: Uint8Array<ArrayBuffer>,
  prk: Uint8Array<ArrayBuffer>,
  label: Uint8Array<ArrayBuffer>,
  info: Uint8Array<ArrayBuffer>,
  length: number
): Promise<Uint8Array<ArrayBuffer>> {
  const labeledInfo = concat([i2osp(length, 2), HPKE_VERSION, suiteId, label, info]);
  return hpkeScheme.expand(prk, labeledInfo, length);
}

async function baseEncap(hpkeScheme: HPKECryptoScheme, pkR: PublicKey): Promise<Encapsulation> {
  const {publicKey: pkE, privateKey: skE} = await hpkeScheme.generateKeyPair();
  await validatePublicKey(pkE);
  const dh = await hpkeScheme.dh(skE, pkR);
  // We need to make sure dh is not the point at infinity, as described in https://www.rfc-editor.org/rfc/rfc9180.html#section-7.1.4.
  // https://vnhacker.blogspot.com/2015/09/why-not-validating-curve25519-public.html for more details.
  // We don't expect this to happen naturally. To keep complexity low, just throw an error when it happens anyway.
  if (dh.every((byte) => byte === 0)) {
    throw new Error('DH result is the point at infinity');
  }
  const enc = await hpkeScheme.serializePublicKey(pkE);
  const serializedPkR = await hpkeScheme.serializePublicKey(pkR);
  const kemContext = concat([enc, serializedPkR]);
  const sharedSecret = await extractAndExpand(hpkeScheme, dh, kemContext);

  return {
    encappedKey: enc,
    sharedSecret,
  };
}

async function authEncap(
  hpkeScheme: HPKECryptoScheme,
  pkR: PublicKey,
  skS: PrivateKey
): Promise<Encapsulation> {
  const pkS = await hpkeScheme.pkFromSk(skS);
  await validatePublicKey(pkS);
  const {publicKey: pkE, privateKey: skE} = await hpkeScheme.generateKeyPair();
  await validatePublicKey(pkE);
  const dh = concat([await hpkeScheme.dh(skE, pkR), await hpkeScheme.dh(skS, pkR)]);
  // We need to make sure dh is not the point at infinity, as described in https://www.rfc-editor.org/rfc/rfc9180.html#section-7.1.4.
  // https://vnhacker.blogspot.com/2015/09/why-not-validating-curve25519-public.html for more details.
  // We don't expect this to happen naturally. To keep complexity low, just throw an error when it happens anyway.
  if (dh.every((byte) => byte === 0)) {
    throw new Error('DH result is the point at infinity');
  }
  const enc = await hpkeScheme.serializePublicKey(pkE);
  const serializedPkR = await hpkeScheme.serializePublicKey(pkR);
  const serializedPkS = await hpkeScheme.serializePublicKey(pkS);
  const kemContext = concat([enc, serializedPkR, serializedPkS]);
  const sharedSecret = await extractAndExpand(hpkeScheme, dh, kemContext);

  return {
    encappedKey: enc,
    sharedSecret,
  };
}

async function baseDecap(
  hpkeScheme: HPKECryptoScheme,
  enc: Uint8Array<ArrayBuffer>,
  skR: PrivateKey
): Promise<Uint8Array<ArrayBuffer>> {
  const pkFromSk = await hpkeScheme.pkFromSk(skR);
  await validatePublicKey(pkFromSk);
  const pkE = await hpkeScheme.deserializePublicKey(enc);
  await validatePublicKey(pkE);
  const dh = await hpkeScheme.dh(skR, pkE);
  // We need to make sure dh is not the point at infinity, as described in https://www.rfc-editor.org/rfc/rfc9180.html#section-7.1.4.
  // https://vnhacker.blogspot.com/2015/09/why-not-validating-curve25519-public.html for more details.
  // We don't expect this to happen naturally. To keep complexity low, just throw an error when it happens anyway.
  if (dh.every((byte) => byte === 0)) {
    throw new Error('DH result is the point at infinity');
  }
  const serializedPkR = await hpkeScheme.serializePublicKey(pkFromSk);
  const kemContext = concat([enc, serializedPkR]);
  const sharedSecret = await extractAndExpand(hpkeScheme, dh, kemContext);

  return sharedSecret;
}

async function authDecap(
  hpkeScheme: HPKECryptoScheme,
  enc: Uint8Array<ArrayBuffer>,
  skR: PrivateKey,
  pkS: PublicKey
): Promise<Uint8Array<ArrayBuffer>> {
  const pkFromSk = await hpkeScheme.pkFromSk(skR);
  await validatePublicKey(pkFromSk);
  const pkE = await hpkeScheme.deserializePublicKey(enc);
  await validatePublicKey(pkE);
  const dh = concat([await hpkeScheme.dh(skR, pkE), await hpkeScheme.dh(skR, pkS)]);
  // We need to make sure dh is not the point at infinity, as described in https://www.rfc-editor.org/rfc/rfc9180.html#section-7.1.4.
  // https://vnhacker.blogspot.com/2015/09/why-not-validating-curve25519-public.html for more details.
  // We don't expect this to happen naturally. To keep complexity low, just throw an error when it happens anyway.
  if (dh.every((byte) => byte === 0)) {
    throw new Error('DH result is the point at infinity');
  }
  const serializedPkR = await hpkeScheme.serializePublicKey(pkFromSk);
  const serializedPkS = await hpkeScheme.serializePublicKey(pkS);
  const kemContext = concat([enc, serializedPkR, serializedPkS]);
  const sharedSecret = await extractAndExpand(hpkeScheme, dh, kemContext);

  return sharedSecret;
}

async function extractAndExpand(
  hpkeScheme: HPKECryptoScheme,
  dh: Uint8Array<ArrayBuffer>,
  kemContext: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  const eaePrk = await labeledExtract(
    hpkeScheme,
    kemSuitId(hpkeScheme),
    new Uint8Array(0),
    new TextEncoder().encode('eae_prk'),
    dh
  );
  const sharedSecret = labeledExpand(
    hpkeScheme,
    kemSuitId(hpkeScheme),
    eaePrk,
    new TextEncoder().encode('shared_secret'),
    kemContext,
    hpkeScheme.info().nSecret
  );

  return sharedSecret;
}

function kemSuitId(hpkeScheme: HPKECryptoScheme): Uint8Array<ArrayBuffer> {
  const kemString = new TextEncoder().encode('KEM');
  const i2ospKem = i2osp(hpkeScheme.info().kemId, 2);
  return concat([kemString, i2ospKem]);
}
